import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, UserPlus } from "lucide-react";

export default function AdminUsers() {
  const { user: me, authedClient } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("Could not load staff list");
    } finally {
      setLoading(false);
    }
  }, [authedClient]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await authedClient().post("/admin/users", form);
      setUsers((prev) => [...prev, data]);
      toast.success(`Created ${data.email}`);
      setOpen(false);
      setForm({ email: "", password: "", name: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) {
      toast.error("You can't delete your own account");
      return;
    }
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await authedClient().delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow mb-2">★ ADMIN</p>
          <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Staff Accounts</h1>
          <p className="text-sm text-[color:var(--ink-soft)] mt-1">
            Provision accounts for staff who need admin access. There is no public signup.
          </p>
        </div>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            className="btn-primary"
            data-testid="admin-users-new-button"
          >
            <Plus className="w-4 h-4" /> New staff
          </button>
        )}
      </div>

      {open && (
        <form
          onSubmit={create}
          className="bg-[color:var(--cream)] rounded-2xl p-6 mb-8 space-y-4"
          data-testid="admin-user-form"
        >
          <h2 className="font-display text-xl text-[color:var(--blue-ink)]">New staff account</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="email"
              required
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="admin-user-email"
              className="rounded-lg border border-[color:var(--line)] px-3 py-2"
            />
            <input
              required
              type="password"
              placeholder="Initial password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="admin-user-password"
              className="rounded-lg border border-[color:var(--line)] px-3 py-2"
            />
            <input
              placeholder="Full name (optional)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="admin-user-name"
              className="rounded-lg border border-[color:var(--line)] px-3 py-2"
            />
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={busy}
              className="btn-primary disabled:opacity-60"
              data-testid="admin-user-create-submit"
            >
              <UserPlus className="w-4 h-4" /> {busy ? "Creating…" : "Create account"}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="btn-ghost">
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-center text-[color:var(--muted)] py-8">Loading…</p>
      ) : (
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="text-left text-[color:var(--muted)] uppercase text-xs tracking-wider">
            <tr>
              <th className="py-3 pr-4">Email</th>
              <th className="py-3 pr-4">Name</th>
              <th className="py-3 pr-4">Role</th>
              <th className="py-3 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[color:var(--line)]">
                <td className="py-3 pr-4 font-medium text-[color:var(--blue-ink)]">
                  {u.email}
                  {u.id === me?.id && (
                    <span className="ml-2 text-xs px-2 py-0.5 bg-[color:var(--cream)] text-[color:var(--blue-deep)] rounded-full">
                      you
                    </span>
                  )}
                </td>
                <td className="py-3 pr-4 text-[color:var(--ink-soft)]">{u.name || "-"}</td>
                <td className="py-3 pr-4">
                  <span className="text-xs px-2 py-1 rounded-full bg-[color:var(--cream)] text-[color:var(--blue-deep)]">
                    {u.role}
                  </span>
                </td>
                <td className="py-3 pr-4 text-right">
                  <button
                    onClick={() => remove(u)}
                    disabled={u.id === me?.id}
                    data-testid={`admin-user-delete-${u.id}`}
                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
